import React, { useEffect, useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoCloseOutline } from "react-icons/io5";
import useApi from "../../Hooks/useApi";
import useTranslations from "../../Hooks/useTranslations";
import translations from "./translations.json";
import "./css/feedbackModal.css";

// ---------------------------------------------------------------------------
// Period helpers
// ---------------------------------------------------------------------------

/** Get current date parts in Brussels timezone */
function brusselsDateParts() {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Brussels",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
    .format(now)
    .split("-");
  return {
    year: Number(parts[0]),
    month: Number(parts[1]),
    day: Number(parts[2]),
  };
}

/** Bi-weekly period: "YYYY-MM-01" or "YYYY-MM-15" */
function currentBiweeklyPeriod() {
  const { year, month, day } = brusselsDateParts();
  const m = String(month).padStart(2, "0");
  const d = day >= 15 ? "15" : "01";
  return `${year}-${m}-${d}`;
}

/** Monthly period: "YYYY-MM" */
function currentMonthPeriod() {
  const { year, month } = brusselsDateParts();
  return `${year}-${String(month).padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// Skip-retry logic
// ---------------------------------------------------------------------------

function shouldShowAfterSkips(skip) {
  if (!skip || skip.count === 0) return true;
  if (skip.count >= 3) return false;
  const last = skip.lastSkippedAt ? new Date(skip.lastSkippedAt) : null;
  if (!last) return true;
  const now = new Date();
  const diffDays = (now.getTime() - last.getTime()) / (1000 * 60 * 60 * 24);
  if (skip.count === 1) return diffDays >= 3;
  if (skip.count === 2) return diffDays >= 7;
  return false;
}

// ---------------------------------------------------------------------------
// Confetti
// ---------------------------------------------------------------------------

const CONFETTI_COLORS = [
  "#f87171", "#fb923c", "#facc15", "#4ade80", "#60a5fa",
  "#a78bfa", "#f472b6", "#34d399", "#fbbf24", "#38bdf8",
];

function Confetti() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 50 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        delay: Math.random() * 0.6,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        rotation: Math.random() * 360,
        size: 6 + Math.random() * 8,
        shape: Math.random() > 0.5 ? "circle" : "square",
      })),
    []
  );

  return (
    <div className="feedback-confetti-overlay">
      {pieces.map((p) => (
        <motion.div
          key={p.id}
          initial={{ y: -20, x: `${p.x}vw`, opacity: 1, rotate: 0 }}
          animate={{ y: "110vh", rotate: p.rotation + 720, opacity: [1, 1, 0] }}
          transition={{ duration: 2.5, delay: p.delay, ease: "easeIn" }}
          style={{
            position: "absolute",
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            borderRadius: p.shape === "circle" ? "50%" : "2px",
          }}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Star rating
// ---------------------------------------------------------------------------

function StarRating({ value, onChange }) {
  const [hover, setHover] = useState(0);

  return (
    <div className="feedback-star-rating">
      {[1, 2, 3, 4, 5].map((star) => {
        const active = star <= (hover || value);
        return (
          <button
            key={star}
            type="button"
            className="feedback-star-button"
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            onClick={() => onChange(star)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill={active ? "#facc15" : "none"}
              stroke={active ? "#facc15" : "#d1d5db"}
              strokeWidth={1.5}
              className="feedback-star-icon"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
              />
            </svg>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FeedbackModal (main export)
// ---------------------------------------------------------------------------

const FeedbackModal = () => {
  const t = useTranslations(translations);
  const api = useApi();

  // State
  const [showImprovement, setShowImprovement] = useState(false);
  const [showSatisfaction, setShowSatisfaction] = useState(false);
  const [improvementText, setImprovementText] = useState("");
  const [satisfactionScore, setSatisfactionScore] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // ---------------------------------------------------------------------------
  // Determine which modals to show (single GET call, cached by useApi)
  // ---------------------------------------------------------------------------

  const checkFeedback = useCallback(async () => {
    try {
      const res = await api.get(
        `${window.baseDomain}api/auth-feedback-answers`
      );
      const fa = res?.data || res || {};

      const biweekly = currentBiweeklyPeriod();
      const monthly = currentMonthPeriod();

      // Check improvement
      const improvementAnswered =
        fa.currentImprovementPeriod === biweekly && fa.lastImprovementAnswer;
      const improvementSkip = fa.lastImprovementSkip
        ? {
            count: fa.improvementSkipCount ?? 0,
            lastSkippedAt:
              typeof fa.lastImprovementSkip === "string"
                ? fa.lastImprovementSkip
                : new Date(fa.lastImprovementSkip).toISOString(),
          }
        : { count: 0, lastSkippedAt: null };

      // Check satisfaction
      const satisfactionAnswered =
        fa.currentSatisfactionMonth === monthly && fa.lastSatisfactionAnswer;
      const satisfactionSkip = fa.lastSatisfactionSkip
        ? {
            count: fa.satisfactionSkipCount ?? 0,
            lastSkippedAt:
              typeof fa.lastSatisfactionSkip === "string"
                ? fa.lastSatisfactionSkip
                : new Date(fa.lastSatisfactionSkip).toISOString(),
          }
        : { count: 0, lastSkippedAt: null };

      const shouldShowImprovement =
        !improvementAnswered && shouldShowAfterSkips(improvementSkip);
      const shouldShowSatisfaction =
        !satisfactionAnswered && shouldShowAfterSkips(satisfactionSkip);

      // Show satisfaction at least 1 day after the last improvement interaction
      const lastImprovementDate =
        fa.lastImprovementAnswer || fa.lastImprovementSkip;
      const improvementTooRecent = lastImprovementDate
        ? (new Date().getTime() - new Date(lastImprovementDate).getTime()) /
            (1000 * 60 * 60 * 24) <
          1
        : false;

      if (shouldShowImprovement) {
        setShowImprovement(true);
      } else if (shouldShowSatisfaction && !improvementTooRecent) {
        setShowSatisfaction(true);
      }
    } catch (err) {
      console.error("FeedbackModal: failed to fetch feedback answers", err);
    } finally {
      setLoaded(true);
    }
  }, [api]);

  useEffect(() => {
    // Small delay so the main page loads first
    const timer = setTimeout(() => {
      checkFeedback();
    }, 2000);
    return () => clearTimeout(timer);
  }, [checkFeedback]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const showSuccessAnimation = useCallback(() => {
    setShowConfetti(true);
    setShowThankYou(true);
    setTimeout(() => {
      setShowConfetti(false);
      setShowThankYou(false);
    }, 3000);
  }, []);

  const handleImprovementSubmit = useCallback(async () => {
    if (!improvementText.trim() || submitting) return;
    setSubmitting(true);
    try {
      await api.post(
        `${window.baseDomain}api/auth-feedback-answers`,
        {
          answer: improvementText.trim(),
          periodStart: currentBiweeklyPeriod(),
        }
      );
      setShowImprovement(false);
      setImprovementText("");
      showSuccessAnimation();
    } catch (err) {
      console.error("FeedbackModal: submit improvement failed", err);
    } finally {
      setSubmitting(false);
    }
  }, [improvementText, submitting, showSuccessAnimation, api]);

  const handleImprovementSkip = useCallback(async () => {
    try {
      await api.post(
        `${window.baseDomain}api/auth-feedback-answers/skip-improvement`,
        { periodStart: currentBiweeklyPeriod() }
      );
    } catch {
      // best effort
    }
    setShowImprovement(false);
  }, [api]);

  const handleSatisfactionSubmit = useCallback(async () => {
    if (satisfactionScore === 0 || submitting) return;
    setSubmitting(true);
    try {
      await api.post(
        `${window.baseDomain}api/auth-feedback-answers/satisfaction`,
        {
          score: satisfactionScore,
          month: currentMonthPeriod(),
        }
      );
      setShowSatisfaction(false);
      setSatisfactionScore(0);
      showSuccessAnimation();
    } catch (err) {
      console.error("FeedbackModal: submit satisfaction failed", err);
    } finally {
      setSubmitting(false);
    }
  }, [satisfactionScore, submitting, showSuccessAnimation, api]);

  const handleSatisfactionSkip = useCallback(async () => {
    try {
      await api.post(
        `${window.baseDomain}api/auth-feedback-answers/skip-satisfaction`,
        { month: currentMonthPeriod() }
      );
    } catch {
      // best effort
    }
    setShowSatisfaction(false);
  }, [api]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (!loaded) return null;

  const renderModal = (isOpen, title, content, onSkip) => {
    if (!isOpen) return null;
    return (
      <div>
        <motion.div
          className="feedback-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="feedback-modal-content"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="feedback-modal-close" onClick={onSkip}>
              <IoCloseOutline size={24} />
            </button>
            <h3 className="feedback-modal-title">{title}</h3>
            {content}
          </motion.div>
        </motion.div>
      </div>
    );
  };

  return (
    <div className="feedback-modal">
      {/* Confetti overlay */}
      <AnimatePresence>{showConfetti && <Confetti />}</AnimatePresence>

      {/* Thank you toast */}
      <AnimatePresence>
        {showThankYou && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.9 }}
            className="feedback-thank-you-toast"
          >
            {t.thankYou}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Improvement Modal */}
      <AnimatePresence>
        {renderModal(
          showImprovement,
          t.improvementTitle,
          <>
            <textarea
              className="feedback-textarea"
              rows={4}
              placeholder={t.improvementPlaceholder}
              value={improvementText}
              onChange={(e) => setImprovementText(e.target.value)}
            />
            <div className="feedback-actions">
              <button
                type="button"
                onClick={handleImprovementSkip}
                className="feedback-btn-skip"
              >
                {t.skip}
              </button>
              <button
                type="button"
                onClick={handleImprovementSubmit}
                disabled={!improvementText.trim() || submitting}
                className="feedback-btn-submit"
              >
                {t.submit}
              </button>
            </div>
          </>,
          handleImprovementSkip
        )}
      </AnimatePresence>

      {/* Satisfaction Modal */}
      <AnimatePresence>
        {renderModal(
          showSatisfaction,
          t.satisfactionTitle,
          <>
            <StarRating value={satisfactionScore} onChange={setSatisfactionScore} />
            <div className="feedback-actions">
              <button
                type="button"
                onClick={handleSatisfactionSkip}
                className="feedback-btn-skip"
              >
                {t.skip}
              </button>
              <button
                type="button"
                onClick={handleSatisfactionSubmit}
                disabled={satisfactionScore === 0 || submitting}
                className="feedback-btn-submit"
              >
                {t.submit}
              </button>
            </div>
          </>,
          handleSatisfactionSkip
        )}
      </AnimatePresence>
    </div>
  );
};

export default FeedbackModal;
